import React, { useEffect, useRef, useState } from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell, Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { AppButtonIcon, AppCardTitle, AppFilterChip, AppProviderChips, AppRetailerBlock, AppRetailerBlockContainer } from '@components'
import { dsColorNew } from "@palette"
import { ALERTS_ICONS } from "Pages/ReportsAlerts/Alerts/@components/Tabs"
import { ALERTS } from "Pages/ReportsAlerts/Alerts/@constants"
import CreateAlertsButton from "Pages/ReportsAlerts/Alerts/Conditions/CreateAlerts"


// Same colours as Pages/ReportsAlerts/Alerts/Conditions/PriceComparison/Table/columns/comparison.jsx
const COMPARATOR = {
	Cheaper: { icon: 'chart-line-down', color: dsColorNew.dangerRed },
	Difference: { icon: 'chart-line-up-down', color: dsColorNew.noteAmber },
	'More Expensive': { icon: 'chart-line-up', color: dsColorNew.successText },
}
const UNIT = {
	'%': { icon: 'percent', tooltip: 'Percentage', color: dsColorNew.black },
	'$': { icon: 'money-bills', tooltip: 'Currency', color: dsColorNew.successText },
}

const DEMO_CONDITIONS = [
	{
		name: 'Spain Cheaper Alert',
		market: 'Spain',
		mainSite: 'rentalspot.demo',
		mainProviders: 'AutoRent, DrivePro',
		comparison: { label: 'Cheaper', value: 15, unit: '%' },
		sites: 'pricefly.demo, eurodrive.demo',
		providers: 'Any',
	},
	{
		name: 'UK Price Diff Monitor',
		market: 'United Kingdom',
		mainSite: 'eurodrive.demo',
		mainProviders: 'SpeedCar',
		comparison: { label: 'Difference', value: 20, unit: '$' },
		sites: 'rentalspot.demo',
		providers: 'SpeedCar, PrimeFleet',
	},
	{
		name: 'France Expensive Check',
		market: 'France',
		mainSite: 'pricefly.demo',
		mainProviders: 'Any',
		comparison: { label: 'More Expensive', value: 25, unit: '%' },
		sites: 'Same sites',
		providers: 'Velocar, FleetMax',
	},
]

// Same legend as the real Price Comparison table (AlertsTableCustomToolbar)
const LEGEND = [
	{ icon: 'percent', label: 'Percentage' },
	{ icon: 'money-bills', label: 'Currency', color: dsColorNew.successText },
	{ icon: 'chart-line-down', label: 'Cheaper', color: dsColorNew.dangerRed },
	{ icon: 'chart-line-up', label: 'More Expensive', color: dsColorNew.successText },
	{ icon: 'chart-line-up-down', label: 'Price Difference', color: dsColorNew.noteAmber },
]

const list = value => value.split(', ')

function Providers({ value }) {
	return value === 'Any'
		? <Typography sx={{ fontSize: 13 }}>Any</Typography>
		: <AppProviderChips providers={list(value)} sx={{ justifyContent: 'center' }}/>
}


// True while a real conditions table with rows (it brings its own create button) is on the page: an empty one
// keeps the demo, so an account with no conditions still has something to tour
function useRealTableShown(rootRef) {
	const [shown, setShown] = useState(false)

	useEffect(() => {
		const hasRows = btn => [...(btn.closest('.MuiPaper-root')?.querySelectorAll('tbody tr') || [])].some(tr => tr.children.length > 1)
		const check = () => setShown([...document.querySelectorAll('[data-tour-id="tour-new-condition"]')].some(btn => !rootRef.current?.contains(btn) && hasRows(btn)))
		check()
		const id = setInterval(check, 500)
		return () => clearInterval(id)
	}, [])

	return shown
}


export default function ConditionsDemoData() {
	const rootRef = useRef(null)
	// the real table arrived after the tour started: it takes the demo's place, so there is one "New condition" button
	const realTableShown = useRealTableShown(rootRef)

	return <Grid container spacing={2} ref={rootRef} sx={realTableShown ? { display: 'none' } : {}}>
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
						<AppCardTitle
							icon={ALERTS_ICONS[ALERTS.PRICE_COMPARISON]}
							title="Alerts: Price Comparison"
							sx={{ '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}
						/>
						<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
							{/* the real toolbar: legend chips (AlertsTableCustomToolbar), then the one create button of the list */}
							<Box data-tour-id="tour-alert-legend" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
								{LEGEND.map(l => <AppFilterChip key={l.label} icon={l.icon} iconSx={l.color ? { color: l.color } : {}} label={l.label}/>)}
							</Box>
							<CreateAlertsButton/>
						</Box>
					</Box>

					<Box sx={{ overflowX: 'auto' }}>
						{/* div-based (no <tbody>): the page shows the demo only while no `.MuiTable-root tbody tr` is on screen, so the demo must not match it itself */}
						<Table size="small" component="div">
							<TableHead component="div">
								<TableRow component="div" sx={{ '& .MuiTableCell-head': { whiteSpace: 'nowrap' } }}>
									<TableCell component="div">Name</TableCell>
									<TableCell component="div">Main Site</TableCell>
									<TableCell component="div">Main Provider</TableCell>
									<TableCell component="div" align="center">Condition</TableCell>
									<TableCell component="div">Sites</TableCell>
									<TableCell component="div">Providers</TableCell>
									<TableCell component="div" align="center">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody component="div">
								{DEMO_CONDITIONS.map((c, idx) => {
									const comparator = COMPARATOR[c.comparison.label]
									const unit = UNIT[c.comparison.unit]

									return <TableRow component="div" key={c.name} sx={{
										backgroundColor: idx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
										'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
										'& .MuiTableCell-body': { py: 1.25, fontSize: 13 },
									}}>
										<TableCell component="div">
											<Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
										</TableCell>

										<TableCell component="div">
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
												<AppRetailerBlock retailer={c.mainSite} country={c.market} sx={{ '& .MuiTypography-root': { fontWeight: 'bold' } }}/>
											</Box>
										</TableCell>

										<TableCell component="div">
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<Providers value={c.mainProviders}/>
											</Box>
										</TableCell>

										<TableCell component="div" align="center">
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
												<Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>{c.comparison.value}</Typography>
												<IconSeeker tooltip={unit.tooltip} icon={unit.icon} sx={{ mx: .7, fontSize: 14, color: unit.color }}/>
												<IconSeeker tooltip={c.comparison.label} icon={comparator.icon} sx={{ fontSize: 14, color: comparator.color }}/>
											</Box>
										</TableCell>

										<TableCell component="div">
											{c.sites === 'Same sites'
												? <Typography sx={{ fontSize: 13, textAlign: 'center' }}>Same sites</Typography>
												: <AppRetailerBlockContainer rows={list(c.sites).map(name => ({ name, market: c.market }))}/>}
										</TableCell>

										<TableCell component="div">
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'start' }}>
												<Providers value={c.providers}/>
											</Box>
										</TableCell>

										<TableCell component="div" align="center">
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<AppButtonIcon icon="pen" tooltip="Edit the alert"/>
												<AppButtonIcon icon="trash-can" tooltip="Delete the alert"/>
											</Box>
										</TableCell>
									</TableRow>
								})}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
